<template>
  <div>
    <separator class="mb-separation" text="Resultado de Contratos" />

    <callout
      v-if="userHasPermissions('provider.contract.add')"
      border-color="primary-1"
      class="mb-3"
      class-main="py-3"
      :has-toggle="false"
    >
      <div class="row d-flex justify-content-center">
        <action-button
          class="border w-40 btn"
          i="fas fa-file-plus color-primary-1"
          text="Presione aquí para añadir contrato"
          @click="onAddContractHandler"
        />
      </div>
    </callout>

    <div v-if="results.length > 0">
      <callout
        v-for="(contract, index) in results"
        :key="index"
        :border-color="
          (contract.amendments || []).length > 0 ? 'primary-1' : ''
        "
        class="mb-3"
        class-main="bg-secondary-13"
        class-panel="p-1"
        :has-toggle="(contract.amendments || []).length > 0"
        :opened="isEditingGroup(contract)"
      >
        <row
          :can-edit-number="canEditContractNumber"
          :editing-numbers="isEditingGroup(contract)"
          :model="contract"
          :number-value="parentNumberDraft"
          @cancel-edit-numbers="cancelEditNumbers"
          @remove="onRemoveContractHandler"
          @save-numbers="onSaveNumbers(contract)"
          @toggle-edit-numbers="onToggleEditNumbers(contract)"
          @update-number="onParentNumberInput"
        />

        <template #edition>
          <callout
            v-for="(amendment, idx) in contract.amendments"
            :key="idx"
            class-main="bg-white mb-2"
            :has-border="false"
            :has-toggle="false"
          >
            <row
              :contract-info="contract"
              :editing-numbers="isEditingGroup(contract)"
              :is-child="true"
              :model="amendment"
              :number-suffix="amendmentSuffixDrafts[amendment.id] || ''"
              :number-value="parentNumberDraft"
              @remove="onRemoveContractHandler"
              @update-number-suffix="onAmendmentSuffixInput(amendment, $event)"
            />
          </callout>
        </template>
      </callout>
    </div>
    <div class="text-right mt-3">
      <action-button
        v-if="contractValidate"
        class="mx-2"
        i="fas fa-file-plus pr-1"
        text="Añadir"
        variant="success"
        @click="onAddContractHandler"
      />
    </div>
  </div>
</template>

<script>
import alert from '@/mixins/alert.mixin';
import Row from './components/RowResult';

import {
  getContracts as _getContracts,
  existsContractForCurrentAcademicYear as _existsContractForCurrentAcademicYear,
  removeContract as _removeContract,
  updateContractNumber as _updateContractNumber,
} from '@/modules/provider/api';

export default {
  name: 'Result',
  components: {
    Row,
  },
  mixins: [alert],
  data() {
    return {
      results: [],
      isOpen: false,
      existsContract: null,
      editingContractId: null,
      parentNumberDraft: '',
      amendmentSuffixDrafts: {},
    };
  },
  computed: {
    contractValidate() {
      return (
        this.existsContract === false &&
        this.userHasPermissions('provider.contract.add') &&
        this.results.length > 0
      );
    },
    companyId() {
      return this.$route.params.companyId;
    },
    canEditContractNumber() {
      return this.userHasPermissions(
        'provider.contract.proposalrequest.editproposalnumber'
      );
    },
  },
  async mounted() {
    await this.getContracts();
    await this.existsContractForCurrentAcademicYear();
  },
  methods: {
    async getContracts() {
      await _getContracts({
        companyId: this.$route.params.companyId,
        fiscalYearId: this.$route.params.fiscalYearId,
      })
        .then((res) => {
          this.results = res.data;
        })
        .catch(() =>
          this.ShowToast(
            'Error',
            'Ocurrió un error cargando los datos',
            'error'
          )
        );
    },
    async onAddContractHandler() {
      this.$router.push({ name: 'contractAddEdit' });
    },
    async existsContractForCurrentAcademicYear() {
      this.existsContract = (
        await _existsContractForCurrentAcademicYear(this.companyId)
      ).data;
    },
    async onRemoveContractHandler({ id }) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar el contrato seleccionado?',
        'Sí',
        'No'
      );

      if (!answer) return;

      try {
        const { data } = await _removeContract(id);
        if (!data) {
          this.ShowToast(
            'Error',
            'Ocurrió un error eliminando el contracto.',
            'error'
          );
          return;
        }

        this.ShowToast(
          'Proceso Exitoso',
          'El contracto ha sido eliminado exitosamente.',
          'success'
        );
        this.getContracts();
      } catch (error) {
        this.ShowToast('Error', error.response.data.message, 'error');
      }
    },
    isEditingGroup(contract) {
      return this.editingContractId === contract.id;
    },
    onToggleEditNumbers(contract) {
      if (this.editingContractId === contract.id) {
        this.cancelEditNumbers();
        return;
      }

      const baseNumber = contract.contractNumber || '';
      const suffixDrafts = {};

      (contract.amendments || []).forEach((amendment) => {
        const amendmentNumber = amendment.contractNumber || '';
        const remainder = amendmentNumber.startsWith(baseNumber)
          ? amendmentNumber.slice(baseNumber.length)
          : amendmentNumber;
        suffixDrafts[amendment.id] = (remainder.startsWith('-')
          ? remainder.slice(1)
          : remainder
        ).toUpperCase();
      });

      this.editingContractId = contract.id;
      this.parentNumberDraft = baseNumber;
      this.amendmentSuffixDrafts = suffixDrafts;
    },
    cancelEditNumbers() {
      this.editingContractId = null;
      this.parentNumberDraft = '';
      this.amendmentSuffixDrafts = {};
    },
    onParentNumberInput(value) {
      this.parentNumberDraft = value;
    },
    onAmendmentSuffixInput(amendment, value) {
      this.$set(
        this.amendmentSuffixDrafts,
        amendment.id,
        (value || '').toUpperCase()
      );
    },
    async onSaveNumbers(contract) {
      const parentValue = (this.parentNumberDraft || '').trim();
      const amendmentEntries = (contract.amendments || []).map((amendment) => {
        const letter = (this.amendmentSuffixDrafts[amendment.id] || '').trim();
        return {
          id: amendment.id,
          original: amendment.contractNumber || '',
          letter,
          value: letter ? `${parentValue}-${letter}` : '',
        };
      });

      if (!parentValue || amendmentEntries.some((entry) => !entry.letter)) {
        this.ShowToast(
          'Error',
          'El número de contrato es requerido para el contrato y todas sus enmiendas.',
          'error'
        );
        return;
      }

      const expectedLetters = amendmentEntries.map((_, i) =>
        String.fromCharCode(65 + i)
      );
      const actualLetters = amendmentEntries
        .map((entry) => entry.letter)
        .sort();

      if (JSON.stringify(actualLetters) !== JSON.stringify(expectedLetters)) {
        this.ShowToast(
          'Error',
          `Las letras de las enmiendas deben ser consecutivas sin saltos (${expectedLetters.join(
            ', '
          )}).`,
          'error'
        );
        return;
      }

      const seenValues = new Map([[parentValue, contract.id]]);
      for (const entry of amendmentEntries) {
        if (seenValues.has(entry.value)) {
          this.ShowToast(
            'Error',
            `El número "${entry.value}" está repetido entre el contrato y sus enmiendas. Favor modificarlo antes de guardar.`,
            'error'
          );
          return;
        }
        seenValues.set(entry.value, entry.id);
      }

      const changed = amendmentEntries.filter(
        (entry) => entry.value !== entry.original
      );

      if (parentValue !== (contract.contractNumber || '')) {
        changed.push({ id: contract.id, value: parentValue });
      }

      if (changed.length === 0) {
        this.cancelEditNumbers();
        return;
      }

      try {
        await Promise.all(
          changed.map((entry) => _updateContractNumber(entry.id, entry.value))
        );

        this.ShowToast(
          'Proceso Exitoso',
          'Los números de contrato fueron actualizados exitosamente.',
          'success'
        );

        this.cancelEditNumbers();
        await this.getContracts();
      } catch (error) {
        this.ShowToast(
          'Error',
          error?.response?.data?.message ||
            'Ocurrió un error guardando los números de contrato.',
          'error'
        );
      }
    },
  },
};
</script>
